import { useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { TopicRecentPetitionsItem, TopicTrendingPetitionsItem } from '#src/app/pages/topic/details/pageContext';
import {
	useTopicPageData,
	useTopicPagePaginatedRecentPetitionsData,
	useTopicPagePaginatedTrendingPetitionsData,
} from '#src/app/pages/topic/details/pageContext';
import { capitalizeTopic } from '#src/app/shared/utils/topic';

type ListSort = 'trending' | 'recent';

type Result = ModelHookResult<
	{
		headingText: string;
		items: readonly TopicRecentPetitionsItem[] | TopicTrendingPetitionsItem[];
		hasNextPage: boolean;
		nextPageLoading: boolean;
		selectedSort: ListSort;
		displayed: boolean;
	},
	{
		loadMore: () => void;
		setSelectedSort: (sort: ListSort) => void;
	}
>;

export function useFilterablePetitionsSection(): Result {
	const {
		data: { name },
	} = useTopicPageData();
	const track = useTracking();
	const model = {
		recent: useTopicPagePaginatedRecentPetitionsData(),
		trending: useTopicPagePaginatedTrendingPetitionsData(),
	};

	const { translatePlural } = useI18n();

	const [selectedSort, setSelectedSort] = useState<ListSort>('trending');

	// `totalCount` is a safe visibility signal here: the middle-end masks each connection's
	// total_count down to items.length whenever a page comes back empty, so totalCount > 0
	// implies there are petitions to render. (The raw count can still be inflated mid-pagination,
	// which only affects the heading copy and is tracked as a separate downstream fix.)
	const hasPetitions = model.trending.data.totalCount > 0 || model.recent.data.totalCount > 0;

	const handleSegmentedControlChange = (id: string) => {
		void track('topic_tab_change', { target_tab: id });
		setSelectedSort(id === 'trending' ? 'trending' : 'recent');
	};

	const handleLoadMore = () => {
		void track('topic_show_more_open_petitions');
		model[selectedSort].actions.loadMore();
	};

	return {
		data: {
			headingText: translatePlural('corgi.topic_page.petition_section.heading', model.trending.data.totalCount, {
				name: capitalizeTopic(name),
				count: model.trending.data.totalCountLocalized,
			}),
			selectedSort,
			...model[selectedSort].data,
			displayed: hasPetitions,
		},
		actions: {
			loadMore: handleLoadMore,
			setSelectedSort: handleSegmentedControlChange,
		},
	};
}
